import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { AuthService } from '../../auth.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { 
  Logout, 
  LoginWithBiodigital, 
  CheckProfile, 
  CheckProfileSuccess, 
  LoginWithGoogle, 
  LoginWithFacebook, 
  LoginFailure 
} from '../../state/auth/auth.actions';
import { AuthState } from '../../state/auth/auth.state';
import { filter, takeUntil } from 'rxjs/operators';
import { CompatibilityService } from 'src/app/shared/compatibility.service';
import { ProfileCheckModel } from '../../models/profile-check.model';
import { SocialUser } from 'angularx-social-login';
import { HttpErrorResponse } from '@angular/common/http';
import { SafeHtml, DomSanitizer } from '@angular/platform-browser';
import { GoogleLoginModel } from '../../models/google-login.model';
import { FacebookLoginModel } from '../../models/facebook-login.model';
import { GroupConfigModel } from '../../models/group-config.model';
import { BiodigitalLoginModel } from '../../models/biodigital-login.model';
import { plainToClass } from 'class-transformer';
import { ActivatedRoute } from '@angular/router';
import { StorageService, StorageItemEnum } from 'src/app/shared/storage.service';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginPageComponent implements OnInit, OnDestroy {
  @Select(AuthState.getProfileCheck)
  profileCheck$: Observable<ProfileCheckModel>;

  @Select(AuthState.getLoading)
  loading$: Observable<boolean>;

  private ngUnsubscribe = new Subject();

  get email() {
    return this.formEmail.get('email');
  }

  get password() {
    return this.formPassword.get('password');
  }

  get group() {
    return this.activatedRoute.snapshot.queryParams.group;
  }

  get code() {
    return this.activatedRoute.snapshot.queryParams.code;
  }

  get group_invite() {
    return this.activatedRoute.snapshot.queryParams.group_invite;
  }

  formEmail: FormGroup;
  formPassword: FormGroup;
  supportsWegGl = true;
  errorMessage: SafeHtml;
  groupConfig: GroupConfigModel;

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private compatibilityService: CompatibilityService,
    private authService: AuthService,
    private actions: Actions,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute
  ) {
    this.groupConfig = this.authService.getGroupConfigFromRoute(this.route.snapshot);

    // get saved email
    const email = this.storageService.getLocalItem(StorageItemEnum.RememberMeEmail) || '';
    const rememberMe = !!email;

    this.formEmail = this.formBuilder.group({
      'email': [email, [Validators.required, Validators.email]],
      'groupCode': [this.groupConfig.groupCode]
    });

    this.formPassword = this.formBuilder.group({
      'password': ['', Validators.required],
      'rememberMe': [rememberMe]
    });
  }

  ngOnInit() {
    this.store.dispatch(new CheckProfileSuccess(null));
    
    this.profileCheck$.pipe(
      filter(profileCheck => profileCheck != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(profileCheck => {
      if (!profileCheck.isUser) {
        this.handleUserNotFound();  
      }
    });

    this.actions.pipe(
      ofActionDispatched(LoginFailure),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: LoginFailure) => {
      this.handleLoginFailure(action.error);
    });

    this.supportsWegGl = this.compatibilityService.supportsWebGl();

    // track remember me
    this.formPassword.get('rememberMe').valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rememberMe) => {
        if (rememberMe && this.formEmail.valid) {
          this.storageService.setLocalItem(StorageItemEnum.RememberMeEmail, this.formEmail.value.email);
        } else {
          this.storageService.removeLocalItem(StorageItemEnum.RememberMeEmail);
        }
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleBackClick() {
    this.store.dispatch(new CheckProfileSuccess(null));
    this.errorMessage = '';
  }

  handleGoogleClick() {
    this.authService.authenticateWithGoogle()
      .then((value: SocialUser) => {
        const login = plainToClass(GoogleLoginModel, {
          ...new GoogleLoginModel(
            value.idToken
          ),
          ...this.groupConfig
        } as Object);

        this.store.dispatch(new LoginWithGoogle(login));
      })
      .catch(() => {});
  }

  // Deprecated
  handleFacebookClick() {
    this.authService.authenticateWithFacebook()
      .then((value: SocialUser) => {
        const login = new FacebookLoginModel(value.authToken);
        this.store.dispatch(new LoginWithFacebook(login));
      })
      .catch(() => {});
  }

  handleEmailSubmit() {
    this.submitEmail();
  }

  handlePasswordSubmit() {
    this.errorMessage = '';
    this.submitPassword();
  }

  handleLogout() {
    this.store.dispatch(new Logout());
  }

  handleLoginFailure(error: HttpErrorResponse) {
    const message: string = error.error.error.message || 'There was an error signing in.';
    this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(message);
  }

  handleUserNotFound() {
    const message = `
      <div class="text-12 text-medium">Account not found</div>
    `;

    this.errorMessage = this.sanitizer.bypassSecurityTrustHtml(message);
  }

  submitEmail() {
    this.errorMessage = '';

    if (this.formEmail.valid) {
      const email = this.formEmail.get('email').value;
      this.store.dispatch(new CheckProfile(email));
    }
  }

  submitPassword() {
    if (this.formPassword.valid) {
      let login = new BiodigitalLoginModel(
        this.formEmail.get('email').value,
        this.formPassword.get('password').value,
        this.formPassword.get('rememberMe').value
      );

      login = {
        ...login,
        ...this.groupConfig
      };

      login.groupCode = this.formEmail.get('groupCode').value;

      this.store.dispatch(new LoginWithBiodigital(login));
    }
  }
}
